
<template>
  <header>
    <!-- Main Navigation Bar -->
    <nav class="bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100 sticky top-0 z-50">
      <div class="container mx-auto px-4 py-3">
        <div class="flex items-center justify-between">
          <!-- Logo -->
          <router-link to="/" class="flex items-center group cursor-pointer">
            <div class="relative">
              <img
                :src="etimadLogo"
                alt="Al Etimad Haramain Travel"
                class="h-14 w-auto transition-transform group-hover:scale-105"
              />
              <div class="absolute -inset-1 bg-amber-500/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity -z-10"></div>
            </div>
          </router-link>

          <!-- Navigation Links -->
          <div class="hidden md:flex items-center space-x-1">
            <router-link
              to="/"
              class="relative px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group"
            >
              <span class="relative z-10">Home</span>
              <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
            </router-link>
            <router-link
              to="/packages"
              class="relative px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group"
            >
              <span class="relative z-10">Packages</span>
              <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
            </router-link>

            <!-- Services Dropdown -->
            <div class="relative dropdown-group">
              <button
                class="relative inline-flex items-center gap-1.5 px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group focus:outline-none"
              >
                <span class="relative z-10">Services</span>
                <svg class="h-4 w-4 relative z-10 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
              </button>
              <div class="dropdown-panel">
                <router-link
                  v-for="service in servicesLinks"
                  :key="service.name"
                  :to="service.path"
                  class="block rounded-lg px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 font-medium transition-all cursor-pointer"
                >
                  {{ service.name }}
                </router-link>
              </div>
            </div>

            <!-- Experiences Dropdown -->
            <div class="relative dropdown-group">
              <button
                class="relative inline-flex items-center gap-1.5 px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group focus:outline-none"
              >
                <span class="relative z-10">Experiences</span>
                <svg class="h-4 w-4 relative z-10 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
              </button>
              <div class="dropdown-panel">
                <router-link
                  v-for="exp in experiencesLinks"
                  :key="exp.name"
                  :to="exp.path"
                  class="block rounded-lg px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 font-medium transition-all cursor-pointer"
                >
                  {{ exp.name }}
                </router-link>
              </div>
            </div>

            <router-link
              to="/about"
              class="relative px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group"
            >
              <span class="relative z-10">About</span>
              <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
            </router-link>

            <router-link
              to="/contact"
              class="relative px-4 py-2 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all group"
            >
              <span class="relative z-10">Contact</span>
              <span class="absolute inset-0 bg-amber-50 rounded-lg scale-0 group-hover:scale-100 transition-transform origin-center"></span>
            </router-link>
          </div>

          <!-- Currency Switcher + Book Now Button -->
          <div class="hidden md:flex items-center gap-3">
            <CurrencySwitcher />
            <button
              class="relative bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-6 py-2.5 rounded-lg font-semibold text-sm shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              @click="openBookingModal"
            >
              <span class="relative z-10 flex items-center space-x-1.5">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Book Now</span>
              </span>
            </button>
          </div>

          <!-- Mobile Menu Button -->
          <button
            @click="toggleMobileMenu"
            class="md:hidden p-2 rounded-lg text-gray-700 hover:bg-amber-50 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="md:hidden mt-4 space-y-2">
          <!-- Home Link -->
          <router-link
            to="/"
            @click="toggleMobileMenu"
            class="block py-2 text-gray-800 hover:text-amber-500 font-medium cursor-pointer"
          >
            Home
          </router-link>

          <!-- Packages Link -->
          <router-link
            to="/packages"
            @click="toggleMobileMenu"
            class="block py-2 text-gray-800 hover:text-amber-500 font-medium cursor-pointer"
          >
            Packages
          </router-link>

          <!-- Services Dropdown (Mobile) -->
          <div class="border-t border-gray-200 pt-2">
            <button
              @click="toggleMobileServices"
              class="flex items-center justify-between w-full py-2 text-gray-800 hover:text-amber-500 font-medium"
            >
              <span>Services</span>
              <svg
                class="h-5 w-5 transition-transform"
                :class="{ 'rotate-180': mobileServicesOpen }"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileServicesOpen"
              class="pl-4 mt-2 space-y-1 border-l-2 border-amber-100"
            >
              <router-link
                v-for="service in servicesLinks"
                :key="service.name"
                :to="service.path"
                @click="toggleMobileMenu"
                class="block py-2 text-sm text-gray-600 hover:text-amber-500 font-medium cursor-pointer"
              >
                {{ service.name }}
              </router-link>
            </div>
          </div>

          <!-- Experiences Dropdown (Mobile) -->
          <div class="border-t border-gray-200 pt-2">
            <button
              @click="toggleMobileExperiences"
              class="flex items-center justify-between w-full py-2 text-gray-800 hover:text-amber-500 font-medium"
            >
              <span>Experiences</span>
              <svg
                class="h-5 w-5 transition-transform"
                :class="{ 'rotate-180': mobileExperiencesOpen }"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileExperiencesOpen"
              class="pl-4 mt-2 space-y-1 border-l-2 border-amber-100"
            >
              <router-link
                v-for="exp in experiencesLinks"
                :key="exp.name"
                :to="exp.path"
                @click="toggleMobileMenu"
                class="block py-2 text-sm text-gray-600 hover:text-amber-500 font-medium cursor-pointer"
              >
                {{ exp.name }}
              </router-link>
            </div>
          </div>

          <!-- About Link -->
          <router-link
            to="/about"
            @click="toggleMobileMenu"
            class="block py-2 text-gray-800 hover:text-amber-500 font-medium border-t border-gray-200 pt-2 cursor-pointer"
          >
            About
          </router-link>

          <!-- Contact Link -->
          <router-link
            to="/contact"
            @click="toggleMobileMenu"
            class="block py-2 text-gray-800 hover:text-amber-500 font-medium border-t border-gray-200 pt-2 cursor-pointer"
          >
            Contact
          </router-link>

          <!-- Currency Switcher (Mobile) -->
          <div class="border-t border-gray-200 pt-3 flex items-center justify-between">
            <span class="text-sm font-medium text-gray-600">Currency</span>
            <CurrencySwitcher />
          </div>

          <!-- Book Now Button -->
          <button
            @click="handleMobileBookNow"
            class="block w-full rounded-lg bg-amber-500 hover:bg-amber-600 py-3 text-center text-white font-medium transition mt-2"
          >
            Book Now
          </button>
        </div>
      </div>
    </nav>

    <BookingModal :open="bookingModalOpen" @close="closeBookingModal" />
  </header>
</template>

<script>
import BookingModal from './BookingModal.vue'
import CurrencySwitcher from './CurrencySwitcher.vue'
import etimadLogo from '../assets/images/etimad-logo-wide.png'

export default {
  name: 'HeaderComponent',
  components: {
    BookingModal,
    CurrencySwitcher
  },
  data() {
    return {
      etimadLogo: etimadLogo,
      mobileMenuOpen: false,
      mobileServicesOpen: false,
      mobileExperiencesOpen: false,
      servicesLinks: [
        { path: '/hotels', name: 'Hotels' },
        { path: '/flights', name: 'Flights' },
        { path: '/services', name: 'Transport' },
        { path: '/services', name: 'Visa' }
      ],
      experiencesLinks: [
        { path: '/ziarat', name: 'Haramain Ziarat' },
        { path: '/gallery', name: 'Gallery' },
        { path: '/reviews', name: 'Reviews' }
      ],
      bookingModalOpen: false
    }
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
      // Reset dropdowns when closing mobile menu
      if (!this.mobileMenuOpen) {
        this.mobileServicesOpen = false
        this.mobileExperiencesOpen = false
      }
    },
    toggleMobileServices() {
      this.mobileServicesOpen = !this.mobileServicesOpen
    },
    toggleMobileExperiences() {
      this.mobileExperiencesOpen = !this.mobileExperiencesOpen
    },
    openBookingModal() {
      this.bookingModalOpen = true
    },
    closeBookingModal() {
      this.bookingModalOpen = false
    },
    handleMobileBookNow() {
      this.toggleMobileMenu()
      this.openBookingModal()
    }
  }
}
</script>

<style scoped>
.dropdown-group:hover .dropdown-panel,
.dropdown-group:focus-within .dropdown-panel {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}

.dropdown-panel {
  @apply invisible absolute top-full mt-2 w-48 rounded-xl border border-gray-100 bg-white/95 backdrop-blur-md p-2 shadow-2xl opacity-0 transition-all;
  transform: translateY(10px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}
</style>
